export enum RegionCode {
  Canada = "CA",
  USA = "US",
  France = "FR",
  UK = "UK",
  Australia = "AU",
  Brazil = "BR",
  Portugal = "PT",
  Germany = "DE",
  Spain = "ES",
  Italy = "IT",
  Mexico = "MX",
  Switzerland = "CH",
  Austria = "AT",
  Belgium = "BE",
  Netherlands = "NL",
  Poland = "PL",
  Sweden = "SE",
  Ireland = "IE",
  NewZealand = "NZ",
  India = "IN",
  Argentina = "AR",
  Colombia = "CO",
  Chile = "CL",
  Japan = "JP",
  All = "ALL",
}
export enum Region {
  USA = "USA",
  Canada = "Canada",
  France = "France",
  UK = "UK",
  Australia = "Australia",
  Brazil = "Brazil",
  Portugal = "Portugal",
  Germany = "Germany",
  Spain = "Spain",
  Italy = "Italy",
  Mexico = "Mexico",
  Switzerland = "Switzerland",
  Austria = "Austria",
  Belgium = "Belgium",
  Netherlands = "Netherlands",
  Poland = "Poland",
  Sweden = "Sweden",
  Ireland = "Ireland",
  NewZealand = "New Zealand",
  India = "India",
  Argentina = "Argentina",
  Colombia = "Colombia",
  Chile = "Chile",
  Japan = "Japan",
  All = "All",
}

export enum RegionDefaultLocale {
  USA = "en-US",
  Canada = "en-CA",
  France = "fr-FR",
  UK = "en-UK",
  Australia = "en-AU",
  Brazil = "pt-BR",
  Portugal = "pt-PT",
  Germany = "de-DE",
  Spain = "es-ES",
  Italy = "it-IT",
  Mexico = "es-MX",
  Switzerland = "de-CH",
  Austria = "de-AT",
  Belgium = "fr-BE",
  Netherlands = "en-NL",
  Poland = "en-PL",
  Sweden = "en-SE",
  Ireland = "en-IE",
  NewZealand = "en-NZ",
  India = "en-IN",
  Argentina = "es-AR",
  Colombia = "es-CO",
  Chile = "es-CL",
  Japan = "ja-JP",
  All = "en-CA",
}

export const BASE_URL: Record<RegionCode, string> = {
  CA: "https://inceptionai.ca",
  US: "https://inceptionia.com",
  FR: "https://www.inceptionai.fr",
  UK: "https://www.inceptionai.uk",
  AU: "https://inceptionai.shop",
  BR: "https://inceptionai.shop",
  PT: "https://inceptionai.shop",
  DE: "https://inceptionai.shop",
  ES: "https://inceptionai.shop",
  IT: "https://inceptionai.shop",
  MX: "https://inceptionai.shop",
  CH: "https://inceptionai.shop",
  AT: "https://inceptionai.shop",
  BE: "https://inceptionai.shop",
  NL: "https://inceptionai.shop",
  PL: "https://inceptionai.shop",
  SE: "https://inceptionai.shop",
  IE: "https://inceptionai.shop",
  NZ: "https://inceptionai.shop",
  IN: "https://inceptionai.shop",
  AR: "https://inceptionai.shop",
  CO: "https://inceptionai.shop",
  CL: "https://inceptionai.shop",
  JP: "https://inceptionai.shop",
  ALL: "https://inceptionai.shop",
};

/**
 * Get the base URL for a specific region and subdomain type
 * @param regionCode - The region code
 * @param isAdult - Whether to get the adult subdomain URL
 * @returns The appropriate base URL
 */
export function getRegionBaseURL(
  regionCode: RegionCode,
  isAdult: boolean = false
): string {
  const baseURL = BASE_URL[regionCode];
  if (!isAdult) return baseURL;

  // Replace https://www. with https://adult. or https:// with https://adult.
  if (baseURL.includes("https://www.")) {
    return baseURL.replace("https://www.", "https://adult.");
  }
  return baseURL.replace("https://", "https://adult.");
}

/** Strip port from Host / x-forwarded-host value; lowercase for comparisons. */
export function normalizeRequestHostname(host: string | null | undefined): string | null {
  if (!host || typeof host !== "string") return null;
  const h = host.trim().split(":")[0]?.toLowerCase();
  return h || null;
}

/**
 * US storefront hostnames. Canonical is `inceptionia.com` (also accessible via
 * `www.`); `inceptionai.shop` and `inceptionaiapp.com` are kept as alternate
 * hosts so historical inbound links and existing crawled URLs keep serving
 * US content without a forced redirect. Case-insensitive.
 */
export function isUsMarketSiteHostname(host: string): boolean {
  const h = host.toLowerCase();
  return (
    h.includes("inceptionia.com") ||
    h.includes("inceptionai.shop") ||
    h.includes("inceptionaiapp.com")
  );
}

/**
 * https origin for sitemap URLs: US uses the request host on shop/app domains so each domain lists its own URLs.
 */
export function getPublicSiteOriginForSitemap(
  regionCode: RegionCode,
  hostname: string | null | undefined,
): string {
  const h = normalizeRequestHostname(hostname);
  if (regionCode === RegionCode.USA && h && isUsMarketSiteHostname(h) && !h.startsWith("adult.")) {
    return `https://${h}`;
  }
  return BASE_URL[regionCode];
}

/**
 * Adult sitemap base URL: prefer the request host so US dual domains (shop vs app) list correct adult URLs.
 */
export function getAdultSitemapOriginFromHostname(hostname: string | null | undefined): string | null {
  const h = normalizeRequestHostname(hostname);
  if (!h || !h.startsWith("adult.")) return null;
  return `https://${h}`;
}

/**
 * Every spelling a region answers to: world-atlas geography names, local
 * endonyms, and the short codes that leak in from geo data. THE one place
 * mapping geography names to a region — `getMapRegionName` and the globe's
 * colour map are both derived from it, so a new country is added here only.
 * Adding a country anywhere else is what let Japan drift out of the globe's
 * hand-maintained copy and render permanently grey.
 */
export const REGION_NAME_ALIASES: Record<Region, readonly string[]> = {
  [Region.USA]: ["USA", "United States", "United States of America", "US"],
  [Region.Canada]: ["Canada"],
  [Region.France]: ["France"],
  [Region.UK]: [
    "UK",
    "United Kingdom",
    "Great Britain",
    "Britain",
    "England",
    "Scotland",
    "Wales",
    "Northern Ireland",
  ],
  [Region.Australia]: ["Australia", "Commonwealth of Australia"],
  [Region.Brazil]: ["Brazil", "Brasil", "BR"],
  [Region.Portugal]: ["Portugal", "PT"],
  [Region.Germany]: ["Germany", "Deutschland", "DE"],
  [Region.Spain]: ["Spain", "España", "ES"],
  [Region.Italy]: ["Italy", "Italia", "IT"],
  [Region.Mexico]: ["Mexico", "México", "MX"],
  [Region.Switzerland]: ["Switzerland", "Schweiz", "Suisse", "Svizzera", "CH"],
  [Region.Austria]: ["Austria", "Österreich", "AT"],
  [Region.Belgium]: ["Belgium", "Belgique", "België", "BE"],
  [Region.Netherlands]: ["Netherlands", "The Netherlands", "Nederland", "NL"],
  [Region.Poland]: ["Poland", "Polska", "PL"],
  [Region.Sweden]: ["Sweden", "Sverige", "SE"],
  [Region.Ireland]: ["Ireland", "Éire", "IE"],
  [Region.NewZealand]: ["New Zealand", "NZ"],
  [Region.India]: ["India", "IN"],
  [Region.Argentina]: ["Argentina", "AR"],
  [Region.Colombia]: ["Colombia", "CO"],
  [Region.Chile]: ["Chile", "CL"],
  [Region.Japan]: ["Japan", "日本", "JP"],
  // "All" is a UI pseudo-region, never a geography.
  [Region.All]: [],
};

const REGION_BY_ALIAS = new Map<string, Region>();
for (const [region, aliases] of Object.entries(REGION_NAME_ALIASES)) {
  for (const alias of aliases) REGION_BY_ALIAS.set(alias, region as Region);
}

/** Every geography spelling that should paint as this region. */
export function getRegionGeoNames(region: Region): readonly string[] {
  return REGION_NAME_ALIASES[region] ?? [];
}

// Utility function to get region name that matches map geography names
export function getMapRegionName(geoName: string): Region | null {
  return REGION_BY_ALIAS.get(geoName) ?? null;
}

/** Region for each region code — the single code/region correspondence table. */
export const REGION_FOR_CODE: Record<RegionCode, Region> = {
  [RegionCode.Canada]: Region.Canada,
  [RegionCode.USA]: Region.USA,
  [RegionCode.France]: Region.France,
  [RegionCode.UK]: Region.UK,
  [RegionCode.Australia]: Region.Australia,
  [RegionCode.Brazil]: Region.Brazil,
  [RegionCode.Portugal]: Region.Portugal,
  [RegionCode.Germany]: Region.Germany,
  [RegionCode.Spain]: Region.Spain,
  [RegionCode.Italy]: Region.Italy,
  [RegionCode.Mexico]: Region.Mexico,
  [RegionCode.Switzerland]: Region.Switzerland,
  [RegionCode.Austria]: Region.Austria,
  [RegionCode.Belgium]: Region.Belgium,
  [RegionCode.Netherlands]: Region.Netherlands,
  [RegionCode.Poland]: Region.Poland,
  [RegionCode.Sweden]: Region.Sweden,
  [RegionCode.Ireland]: Region.Ireland,
  [RegionCode.NewZealand]: Region.NewZealand,
  [RegionCode.India]: Region.India,
  [RegionCode.Argentina]: Region.Argentina,
  [RegionCode.Colombia]: Region.Colombia,
  [RegionCode.Chile]: Region.Chile,
  [RegionCode.Japan]: Region.Japan,
  [RegionCode.All]: Region.All,
};

/**
 * Region for a country code. Accepts the ISO alpha-2 GB alongside the internal
 * UK; "ALL" is not a country and resolves to null.
 */
export function getRegionForCountryCode(countryCode: string): Region | null {
  if (!countryCode) return null;
  const code = countryCode.trim().toUpperCase();
  const normalized = code === "GB" ? RegionCode.UK : code;
  const region = REGION_FOR_CODE[normalized as RegionCode];
  return region && region !== Region.All ? region : null;
}

/**
 * Region label for UI chrome. Only the UK differs from the enum value, which
 * reads as a bare acronym next to full country names.
 */
export function getRegionDisplayName(region: Region | string): string {
  return region === Region.UK ? "United Kingdom" : region;
}

/**
 * Full English country name for prose and AI prompts ("United States", not the
 * enum's "USA"). Distinct from `getRegionDisplayName`, which keeps the shorter
 * UI spelling.
 */
export function getCountryDisplayName(countryCode: string): string | null {
  const region = getRegionForCountryCode(countryCode);
  if (!region) return null;
  if (region === Region.USA) return "United States";
  if (region === Region.UK) return "United Kingdom";
  return region;
}

export const getRegionPartnerTag = (region: Region): string => {
  switch (region) {
    case Region.Canada:
      return "inceptionaica-20";
    case Region["USA"]:
      return "inceptionai04-20";
    case Region.France:
      return "inceptionaifr-21";
    case Region.UK:
      // Canonical UK Associates tag. The legacy "inceptionai05-21" value was
      // stamped fleet-wide and does not credit the UK store — do not revert
      // without reconciling against Associates Central tracking IDs.
      return "inceptionaiuk-21";
    case Region.Australia:
      return "inceptionaiau-21";
    case Region.Germany:
      return "inceptionaide-21"; // Amazon DE partner tag
    case Region.Spain:
      return "inceptionai09-21"; // Amazon ES partner tag
    case Region.Italy:
      return "inceptionai00-21"; // Amazon IT partner tag
    case Region.Mexico:
      return "inceptionai04-20"; // Amazon MX partner tag
    case Region.Switzerland:
      return "inceptionai-21"; // EU fallback
    case Region.Austria:
      return "inceptionaide-21"; // Amazon DE partner tag (shares with DE)
    case Region.Belgium:
      return "inceptionai-21"; // Amazon BE partner tag
    case Region.Netherlands:
      // US Associate ID (inceptionai04-20) — Earn Globally: NL, PL, SE
      return "inceptionai04-20";
    case Region.Poland:
      // US Associate ID (inceptionai04-20) — Earn Globally
      return "inceptionai04-20";
    case Region.Sweden:
      // US Associate ID (inceptionai04-20) — Earn Globally
      return "inceptionai04-20";
    case Region.Ireland:
      return "inceptionaiuk-21"; // Amazon UK partner tag (shares with UK)
    case Region.NewZealand:
      return "inceptionaiau-21"; // Shares with AU
    case Region.India:
      return "inceptionai04-20"; // Amazon IN partner tag
    case Region.Argentina:
      return "inceptionai04-20"; // US fallback
    case Region.Colombia:
      return "inceptionai04-20"; // US fallback
    case Region.Chile:
      return "inceptionai04-20"; // US fallback
    case Region.Japan:
      return "inceptionai04-20"; // Amazon JP partner tag
    case Region.Brazil:
      return "inceptionai04-20";
    case Region.Portugal:
      return "inceptionai04-20";
    default:
      return "inceptionai04-20"; // Default fallback partner tag
  }
};

/**
 * Regions that require explicit advertising consent (GDPR, UK GDPR).
 * Canada (PIPEDA), Australia, and US (outside California/CCPA) do not require strict opt-in.
 * Note: CCPA applies to California only; state-level detection would be needed to scope US.
 */
export const AD_CONSENT_REQUIRED_REGIONS: RegionCode[] = [
  RegionCode.France,       // GDPR
  RegionCode.UK,           // UK GDPR
  RegionCode.Germany,      // GDPR
  RegionCode.Spain,        // GDPR
  RegionCode.Italy,        // GDPR
  RegionCode.Switzerland,  // nDSG (Swiss GDPR-equivalent)
  RegionCode.Austria,      // GDPR
  RegionCode.Belgium,      // GDPR
  RegionCode.Netherlands,  // GDPR
  RegionCode.Poland,      // GDPR
  RegionCode.Sweden,      // GDPR
  RegionCode.Ireland,      // GDPR
  RegionCode.Portugal,     // GDPR
];

export function requiresAdConsent(regionCode: RegionCode | string | undefined): boolean {
  if (!regionCode || regionCode === RegionCode.All) return false;
  return AD_CONSENT_REQUIRED_REGIONS.includes(regionCode as RegionCode);
}

/**
 * Check if a region represents an EU country
 * @param region - The region to check
 * @returns true if the region is in the EU
 */
export function isEURegionByRegion(region: Region): boolean {
  switch (region) {
    case Region.France:
    case Region.Germany:
    case Region.Spain:
    case Region.Italy:
    case Region.Austria:
    case Region.Belgium:
    case Region.Netherlands:
    case Region.Poland:
    case Region.Sweden:
    case Region.Ireland:
    case Region.Portugal:
      return true;
    default:
      return false;
  }
}
